import { generateLineSegment, MassMoments } from "./pca";

function makeEdgeId(from, to) {
  //Direction doesn't matter for the mapping we use this edge ID for, so
  //we generate the same ID for both directions.
  if (from.id < to.id) {
    return `${from.id}/${to.id}`;
  } else {
    return `${to.id}/${from.id}`;
  }
}

function tooShort(p1, p2) {
  return p1.distanceToSquared(p2) < 1e-4;
}


class GeomNode {
  constructor(e) {
    this.element = e;
    this.in = [];
    this.out = [];
    this.spineSegment = null;
  }

  _addEdge(e, l) {
    const idx = l.indexOf(e);
    if (idx === -1) {
      l.push(e);
    }
  }

  addIn(e) {
    this._addEdge(e, this.in);
  }

  addOut(e) {
    this._addEdge(e, this.out);
  }
}


export class TracePaths {

  constructor(key, startElement, endElements, poiElements) {
    this.key = key;
    this.startElement = startElement;
    this.endElements = endElements;
    this.poiElements = poiElements;
    this.allPaths = startElement.traceAllPathsTo(endElements);
  }

  needsLabel(facility, element) {
    const { model, dbId } = element;
    const traceInfo = this.poiElements;
    const isPoi = traceInfo.get(element)?.name;

    //TODO: this check should be avoided by narrowing down the initial list of POIs
    //to begin with. It seems to be useful for skipping labels for rooms and streams (which are already labeled)
    //but that's about it.
    const isSkipped = facility.facetsManager.skipElement(
      model.getData().dbId2flags[dbId],
      model.getData().dbId2catId[dbId]
    );
    return isPoi && !isSkipped;
  }

}

export class FilteredTracePaths {

  constructor(tracePaths, selectedElements) {
    this.tracePaths = tracePaths;
    this.filterTrace(selectedElements);
  }

  filterTrace(selectedElements) {

    const allPaths = this.tracePaths.allPaths;
    let withSelectedElements;
    let elementsOnSelectedPaths = new Set();
    let elementsOffSelectedPaths = new Set();
    if (selectedElements?.size) {
      withSelectedElements = new Map();
      for (const [endElement, paths] of allPaths) {
        const filteredPaths = paths.filter((path) => {
          for (const element of path) {
            if (selectedElements.has(element)) {
              return true;
            }
          }
          return false;
        });
        if (filteredPaths.length) {
          withSelectedElements.set(endElement, filteredPaths);
        }
        for (const path of filteredPaths) {
          for (const element of path) {
            elementsOnSelectedPaths.add(element);
          }
        }
      }
      for (const paths of allPaths.values()) {
        for (const path of paths) {
          for (const element of path) {
            if (!elementsOnSelectedPaths.has(element)) {
              elementsOffSelectedPaths.add(element);
            }
          }
        }
      }
    } else {
      for (const paths of allPaths.values()) {
        for (const path of paths) {
          for (const element of path) {
            elementsOnSelectedPaths.add(element);
          }
        }
      }
    }

    this.filteredBySelection = withSelectedElements;
    this.elementsOnSelectedPaths = elementsOnSelectedPaths;
    this.elementsOffSelectedPaths = elementsOffSelectedPaths;
  }

  #generateTracedSubgraphs(tracePaths) {
    //For each node, collect incoming and outgoing edges, which we have to visualize
    //Create two separate sub-graphs, one containing primary (shortest) trace paths and
    //a second containing longer branches that also lead from source to target(s).
    //The two sub-graphs will be visualized differently.
    const geomNodesPrimary = new Map();
    const geomNodesSecondary = new Map();

    for (const paths of tracePaths.values()) {

      for (let i = 0; i < paths.length; i++) {

        const path = paths[i];
        //Shortest path (the first one, since they are sorted) has high priority.
        //Paths with the same length as the shortest have high priority.
        //All other paths have lower priority.
        const priority = i === 0 || path.length === paths[0].length ? 0 : 1;

        const targetGraph = priority === 0 ? geomNodesPrimary : geomNodesSecondary;

        for (let j = 1; j < path.length; j++) {

          const from = path[j - 1];
          const to = path[j];

          let gFrom = targetGraph.get(from);
          if (!gFrom) {
            gFrom = new GeomNode(from);
            targetGraph.set(from, gFrom);
          }

          let gTo = targetGraph.get(to);
          if (!gTo) {
            gTo = new GeomNode(to);
            targetGraph.set(to, gTo);
          }

          gFrom.addOut(gTo);
          gTo.addIn(gFrom);
        }
      }
    }

    return { geomNodesPrimary, geomNodesSecondary };
  }

  #deriveSpineSegments(geomNodes) {

    const tmp1 = new THREE.Vector3();
    const tmp2 = new THREE.Vector3();

    for (const gn of geomNodes.values()) {

      const c = gn.element.getCenterPoint(tmp1);
      if (!c) continue;

      //Use principal axis derivation if we have enough entry and exit points
      {
        const pts = [];
        const mm = new MassMoments();
        for (const incoming of gn.in) {
          const pt = incoming.element.getIntersectionPoint(gn.element);
          if (!pt) continue;
          mm.addPoint(pt);
          pts.push(pt);
        }

        for (const outgoing of gn.out) {
          const pt = outgoing.element.getIntersectionPoint(gn.element);
          if (!pt) continue;
          mm.addPoint(pt);
          pts.push(pt);
        }

        if (pts.length > 2) {
          try {
            const principalAxis = mm.getPrincipalAxis(tmp2);
            const { startPoint, endPoint } = generateLineSegment(pts, gn.in.length, principalAxis, c);

            const seg = new THREE.Line3(startPoint, endPoint);

            if (!tooShort(seg.start, seg.end)) {
              gn.spineSegment = seg;
            }
            continue;
          } catch (e) {


            //There are cases where PCA can fail and throw
            //console.error(e);
          }}}

      //Find the central segment for this node by computing weighted averages of all input and output points
      let ptStartMid = new THREE.Vector3();
      let ptEndMid = new THREE.Vector3();
      let isEmpty = true;

      {
        let totalWeight = 0;
        for (const incoming of gn.in) {
          isEmpty = false;
          const pt = incoming.element.getIntersectionPoint(gn.element);
          if (!pt) continue;
          tmp2.copy(pt);
          let w = c.distanceToSquared(pt);
          //This is weighted by 4th power of distance to the center
          w *= w;
          tmp2.multiplyScalar(w);
          ptStartMid.add(tmp2);
          totalWeight += w;
        }
        if (totalWeight > 0) {
          ptStartMid.multiplyScalar(1.0 / totalWeight);
        } else {
          ptStartMid = null;
        }
      }

      {
        let totalWeight = 0;
        for (const outgoing of gn.out) {
          isEmpty = false;
          const pt = outgoing.element.getIntersectionPoint(gn.element);
          if (!pt) continue;
          tmp2.copy(pt);
          let w = c.distanceToSquared(pt);
          //This is weighted by 4th power of distance to the center
          w *= w;
          tmp2.multiplyScalar(w);
          ptEndMid.add(tmp2);
          totalWeight += w;
        }
        if (totalWeight > 0) {
          ptEndMid.multiplyScalar(1.0 / totalWeight);
        } else {
          ptEndMid = null;
        }
      }

      if (ptStartMid && ptEndMid && !tooShort(ptStartMid, ptEndMid)) {
        gn.spineSegment = new THREE.Line3(ptStartMid, ptEndMid);
      }
    }
  }

  collectEdges() {

    const tracePaths = this.filteredBySelection || this.tracePaths.allPaths;

    const { geomNodesPrimary, geomNodesSecondary } = this.#generateTracedSubgraphs(tracePaths);

    this.#deriveSpineSegments(geomNodesPrimary);
    this.#deriveSpineSegments(geomNodesSecondary);

    //For each node of each sub-graph, generate segments to draw for visualizing the trace path
    const nodePairs = [];
    const nodePairsSecondary = [];
    const tmppt = new THREE.Vector3();
    const tmppt2 = new THREE.Vector3();

    //Generate graphical edges for primary/shortest paths subgraph
    {
      for (const gn of geomNodesPrimary.values()) {

        const c = gn.element.getCenterPoint();

        const segment = gn.spineSegment;

        //Start or end node, use center point
        if (!segment) {
          for (const incoming of gn.in) {
            const pt = incoming.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            nodePairs.push(pt, c);
          }

          for (const outgoing of gn.out) {
            const pt = outgoing.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            nodePairs.push(c, pt);
          }
        } else {
          //Inner nodes/edges
          //Draw branches off of a main segment determined by the weighted centroids
          //of the input and output points (computed above).
          nodePairs.push(segment.start, segment.end);

          for (const incoming of gn.in) {
            const pt = incoming.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            const closest = segment.closestPointToPoint(pt, true);
            if (tooShort(pt, closest)) {
              continue;
            }
            nodePairs.push(pt, closest);
          }

          for (const outgoing of gn.out) {
            const pt = outgoing.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            const closest = segment.closestPointToPoint(pt, true);
            if (tooShort(pt, closest)) {
              continue;
            }
            nodePairs.push(closest, pt);
          }
        }
      }
    }

    //Generate graphical edges for secondary/low priority paths
    //The logic here is more complex, because it tries to clamp edges to any primary edges (added above)
    //that overlap within the same element
    {
      const hasPrimaryEdge = (from, to) => {
        const gn = geomNodesPrimary.get(from);
        if (!gn) {
          return false;
        }
        return !!gn.out.find((outgoing) => outgoing.element === to);
      };

      const alongSegment = (segment, t, clamp, dst) => {
        if (clamp) {
          t = THREE.Math.clamp(t, 0, 1);
        }
        return segment.delta(dst || new THREE.Vector3()).multiplyScalar(t).add(segment.start);
      };

      for (const gn of geomNodesSecondary.values()) {

        const c = gn.element.getCenterPoint();

        const segment = gn.spineSegment;

        //Start or end node, use center point
        if (!segment) {
          for (const incoming of gn.in) {
            if (hasPrimaryEdge(incoming.element, gn.element)) {
              continue;
            }
            const pt = incoming.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            nodePairsSecondary.push(pt, c);
          }

          for (const outgoing of gn.out) {
            if (hasPrimaryEdge(gn.element, outgoing.element)) {
              continue;
            }
            const pt = outgoing.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            nodePairsSecondary.push(c, pt);
          }
        } else {
          //Inner nodes/edges
          //Draw branches off of a main segment determined by the weighted centroids
          //of the input and output points (computed above).
          let spinesAreDifferent = false;
          let segTooShort = false;
          const gnPrimary = geomNodesPrimary.get(gn.element);
          const mainSegPrimary = gnPrimary?.spineSegment;
          if (mainSegPrimary) {
            if (tooShort(mainSegPrimary.start, segment.start) && tooShort(mainSegPrimary.end, segment.end)) {

              //complete overlap of the primary and secondary spine segments -- we will not draw anything
            } else {//partial overlap -- adjust the main segment of the secondary path to be
              //co-linear with the main segment of the previously computed primary path
              //NOTE that they can still be  going in opposite directions.
              spinesAreDifferent = true;
              mainSegPrimary.closestPointToPoint(segment.start, true, segment.start);
              mainSegPrimary.closestPointToPoint(segment.end, true, segment.end);
              segTooShort = tooShort(segment.start, segment.end);
            }
          } else {
            spinesAreDifferent = true;
          }

          let minT = Infinity;
          let maxT = -Infinity;

          for (const incoming of gn.in) {
            const pt = incoming.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            let t = 0;
            if (!segTooShort) {
              t = segment.closestPointToPointParameter(pt, false);
              if (t > maxT) maxT = t;
              if (t < minT) minT = t;
            }

            if (hasPrimaryEdge(incoming.element, gn.element)) {
              continue;
            }

            const closest = alongSegment(segment, t, true);
            if (!tooShort(pt, closest)) {
              nodePairsSecondary.push(pt, closest);
            }
          }

          for (const outgoing of gn.out) {
            const pt = outgoing.element.getIntersectionPoint(gn.element);
            if (!pt) continue;
            let t = 0;
            if (!segTooShort) {
              t = segment.closestPointToPointParameter(pt, false);
              if (t > maxT) maxT = t;
              if (t < minT) minT = t;
            }

            if (hasPrimaryEdge(gn.element, outgoing.element)) {
              continue;
            }

            const closest = alongSegment(segment, t, true);
            if (!tooShort(pt, closest)) {
              nodePairsSecondary.push(closest, pt);
            }
          }

          if (spinesAreDifferent) {
            if (mainSegPrimary) {
              //Clip the main segment of the secondary path to be no longer than
              //the primary segment.
              let adjStart = alongSegment(segment, minT);
              let adjEnd = alongSegment(segment, maxT);

              tmppt.subVectors(adjEnd, adjStart);
              mainSegPrimary.delta(tmppt2);
              if (tmppt.dot(tmppt2) < 0.0) {
                mainSegPrimary.closestPointToPoint(adjStart, true, adjStart);
                mainSegPrimary.closestPointToPoint(adjEnd, true, adjEnd);
                nodePairsSecondary.push(adjStart, adjEnd);
              }
            } else {
              nodePairsSecondary.push(segment.start, segment.end);
            }
          }
        }
      }
    }

    return { nodePairs, nodePairsSecondary };

  }
}